import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { AppDiscoverabilityCard } from "./app-discoverability-cta/app-discoverability-card"
import { AppDiscoverabilityButton } from "./app-discoverability-cta/app-discoverability-button"
import { useClickedPreviously } from "./app-discoverability-cta/use-clicked-previously"
import { analytics } from "utils/analytics"

// Helps map the env filters that should be applied in the app store based on where the
// component is rendered.
const ORIGINS = {
  editor: "content_blocks,media_gallery",
  mediaGallery: "media_gallery",
  va: "vas"
}

const buildAppStoreHref = origin => {
  const env = ORIGINS[origin]
  return `/apps?env=${env}&return_to=${window.location.href}`
}

/**
 * A reusable component that promotes app discoverability with a call-to-action
 * to browse more apps. Used across multiple pages to encourage users to
 * explore additional apps.
 */
export function AppDiscoverabilityCTA({
  variant,
  size = "md",
  origin,
  className
}) {
  const [hasDismissed, setDismissed, dismissedAt, setDismissedAt] =
    useClickedPreviously(origin)

  const handleClick = () => {
    analytics.track("Element", "Clicked", {
      name: "app_discoverability_cta",
      location: origin
    })
  }

  const handleDismiss = () => {
    analytics.track("Element", "Clicked", {
      name: "app_discoverability_cta_dismiss",
      location: origin
    })
    setDismissed()
  }

  // Reset the dismissed state for users that clicked before dismissing was a separate
  // button. Skips if dismissed within the last hour and the cutoff hasn't passed yet.
  useEffect(() => {
    const dismissedAtDate = dismissedAt && new Date(dismissedAt)
    const cutoffDate = new Date("2025-11-07")
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000)

    if (
      dismissedAtDate &&
      dismissedAtDate < cutoffDate &&
      dismissedAtDate < oneHourAgo
    ) {
      setDismissedAt(null)
    }
  }, [dismissedAt, setDismissedAt])

  if (hasDismissed) {
    return null
  }

  const href = buildAppStoreHref(origin)

  if (variant === "card") {
    return (
      <AppDiscoverabilityCard
        size={size}
        href={href}
        onClick={handleClick}
        onDismiss={handleDismiss}
        className={className}
      />
    )
  } else if (variant === "footer") {
    return (
      <AppDiscoverabilityButton
        href={href}
        onClick={handleClick}
        onDismiss={handleDismiss}
        className={className}
      />
    )
  }
}

AppDiscoverabilityCTA.propTypes = {
  size: PropTypes.oneOf(["sm", "md"]),
  variant: PropTypes.oneOf(["card", "footer"]).isRequired,
  origin: PropTypes.oneOf(Object.keys(ORIGINS)).isRequired,
  href: PropTypes.string,
  className: PropTypes.string
}
